/**
 * Lead Conversion Funnel — visualizes the prospect-to-sign journey.
 * Reads from /api/leads/dashboard (uses pipeline array already returned).
 *
 * Stages (in funnel order):
 *  New Lead → Cold → Prospective → Hot → New Sign → Existing Account
 *
 * Each stage shows count + drop-off % vs. previous stage.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingDown } from "lucide-react";
import { API } from "../../lib/api";
import { fmtN } from "../../lib/format";

const FUNNEL_STAGES = [
  { key: "New Lead",          label: "New Lead",          color: "#94a3b8" },
  { key: "Cold Prospect",     label: "Cold Prospect",     color: "#60a5fa" },
  { key: "Prospective",       label: "Prospective",       color: "#3b82f6" },
  { key: "Hot Prospect",      label: "Hot Prospect",      color: "#f59e0b" },
  { key: "New Account Sign",  label: "New Account Sign",  color: "#10b981" },
  { key: "Existing Account",  label: "Existing Account",  color: "#059669" },
];

const LEAKAGE_STAGES = [
  { key: "Lost Lead",     label: "Lost Lead (batal sebelum join)", color: "#E74C3C" },
  { key: "Churn Account", label: "Churn Account (kontrak berakhir/putus)", color: "#8E44AD" },
];

export default function ConversionFunnel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/leads/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data) return null;
  const byStatus = Object.fromEntries((data.pipeline || []).map((p) => [p.status, p.count]));
  const max = Math.max(...FUNNEL_STAGES.map((s) => byStatus[s.key] || 0), 1);
  const topCount = byStatus["New Lead"] || Math.max(...FUNNEL_STAGES.map((s) => byStatus[s.key] || 0), 1);

  // Final conversion: Existing+Sign / total non-lost
  const total = (data.pipeline || []).reduce((sum, p) => sum + p.count, 0);
  const signed = (byStatus["New Account Sign"] || 0) + (byStatus["Existing Account"] || 0);
  const finalConv = total > 0 ? ((signed / total) * 100).toFixed(1) : "0.0";

  return (
    <div className="card" data-testid="conversion-funnel">
      <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <TrendingDown size={14} color="var(--accent)" />
        <strong>Conversion Funnel · Lead Journey (detail)</strong>
        <span className="badge" style={{ marginLeft: "auto" }}>
          {finalConv}% sign-rate (Sign+Existing / Total)
        </span>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {FUNNEL_STAGES.map((s, idx) => {
          const count = byStatus[s.key] || 0;
          const widthPct = Math.max(8, Math.round((count / max) * 100));
          const prev = idx > 0 ? byStatus[FUNNEL_STAGES[idx - 1].key] || 0 : null;
          const drop = prev && prev > 0 ? Math.round(((prev - count) / prev) * 100) : null;
          const overall = topCount > 0 ? Math.round((count / topCount) * 100) : 0;
          return (
            <div key={s.key} data-testid={`funnel-${s.key.replace(/\s+/g, "-").toLowerCase()}`}
              style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 150, fontSize: 12, fontWeight: 600, color: "var(--text)" }}>
                {s.label}
              </div>
              <div style={{ flex: 1, position: "relative", height: 32, background: "var(--surface-2)", borderRadius: 6 }}>
                <div style={{
                  height: "100%", width: `${widthPct}%`, background: s.color,
                  borderRadius: 6, transition: "width .4s ease",
                  display: "flex", alignItems: "center", paddingLeft: 12,
                  color: "#fff", fontWeight: 700, fontSize: 13,
                }}>
                  {fmtN(count)}
                </div>
              </div>
              <div style={{ width: 128, fontSize: 11, color: "var(--muted)", textAlign: "right" }}>
                <span style={{ fontWeight: 700, color: "var(--navy)" }}>{overall}%</span>
                <span style={{ margin: "0 4px" }}>·</span>
                {drop !== null ? (
                  <span style={{ color: drop > 50 ? "var(--danger)" : drop > 20 ? "var(--warn)" : "var(--muted)" }}>
                    {drop > 0 ? `↓ ${drop}%` : drop < 0 ? `↑ ${Math.abs(drop)}%` : "—"}
                  </span>
                ) : <span>base</span>}
              </div>
            </div>
          );
        })}

        {/* Leakage: Lost Lead vs Churn (dibedakan secara eksplisit) */}
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border-2)",
                      fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Kebocoran Pipeline (bukan bagian funnel maju)
        </div>
        {LEAKAGE_STAGES.map((s) => {
          const count = byStatus[s.key] || 0;
          const widthPct = Math.max(6, Math.round((count / max) * 100));
          return (
            <div key={s.key} data-testid={`funnel-leak-${s.key.replace(/\s+/g, "-").toLowerCase()}`}
              style={{ display: "flex", alignItems: "center", gap: 12, opacity: 0.92 }}>
              <div style={{ width: 150, fontSize: 11, fontWeight: 600, color: s.color }}>{s.label}</div>
              <div style={{ flex: 1, position: "relative", height: 26, background: "var(--surface-2)", borderRadius: 6 }}>
                <div style={{
                  height: "100%", width: `${widthPct}%`, background: s.color,
                  borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 12,
                  color: "#fff", fontWeight: 700, fontSize: 12,
                }}>{fmtN(count)}</div>
              </div>
              <div style={{ width: 128 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
